import * as angular from 'angular';
import * as _ from 'lodash';

import {
    ActualDataType,
    ContextualField,
    DigitalSet,
    Group,
    Identifier,
    IFrequency,
    IValueViewModel,
    PropertyType
} from '../../../../models';

import { getCommonProperties } from './commonProperties';
import { targetProperty } from './targetProperty';
import { linkedGuidProperty, filterViewModels } from './linkedGuidProperty';
import * as Fields from '../../../../common/helpers/contextual';
import {
    DigitalSetGuid,
    configureVariableTypeProperties,
    variableProperties,
} from '../../../../common/helpers/contextual';

const actualDataTypeName = 'dataType';

function getActualDataTypeField() {
    const actualDataTypeField = angular.copy(Fields.ActualDataTypeField);
    actualDataTypeField.modelName = actualDataTypeName;
    return actualDataTypeField;
}

function getAggregationPeriodField() {
    const field = angular.copy(Fields.AggregationPeriod);
    field.modelName = 'aggregationPeriod';
    field.extraData = {
        allowFrequencyIntervalZero: true,
        allowFrequencyIntervalEmpty: true,
        disableFrequencyValidation: false,
        frequencyValidationRules: [
            {
                key: 'format',
                isValid: freq => {
                    const expr = new RegExp('^[0-9]*$');
                    const isValid = expr.test(freq?.interval?.toString() ?? '');
                    return isValid;
                }
            }
        ]
    };
    return field;
}

const calculatedProperties: ContextualField[] =
    getCommonProperties()
        .concat(
            [
                getActualDataTypeField(),
                angular.copy(Fields.DigitalSetGuid),
                {
                    modelName: 'aggregationMode',
                    propertyType: PropertyType.AggregationMode,
                    label: 'Aggregation mode',
                    propertyName: 'aggregationModeProperty',
                    icon: 'fa-calculator'
                },
                linkedGuidProperty,
                getAggregationPeriodField(),
                {
                    modelName: 'isRolling',
                    propertyType: PropertyType.Boolean,
                    label: 'Rolling aggregation',
                    propertyName: 'isRollingProperty',
                    icon: 'fa-circle-o-notch',
                    helpReference: '_Model_configuration/Configuring_identifiers/#aggregation-period'
                }
            ])
        .concat(variableProperties)
        .concat(
            [
                angular.copy(Fields.DefaultValue),
                targetProperty,
                angular.copy(Fields.Resolution),
                angular.copy(Fields.ResolutionPercentage),
                angular.copy(Fields.Min),
                angular.copy(Fields.Max)
            ]);

export function getCalculatedProperties(selectedItem: Identifier, group: Group, digitalSets: DigitalSet[], defaultFrequency : IFrequency): ContextualField[] {
    const properties = calculatedProperties;
    const isDigital = selectedItem.dataType === ActualDataType.Digital;
    const isTimestamp = selectedItem.dataType === ActualDataType.Timestamp;
    const isString = selectedItem.dataType === ActualDataType.String;
    const hasNoVariables = isDigital || isTimestamp || isString;

    variableProperties.forEach(v => v.hide = hasNoVariables);
    configureVariableTypeProperties(properties, selectedItem);

    const linkedGuid = _.find(properties, p => p.modelName === 'linkedGuid');
    if (linkedGuid) {
        linkedGuid.extraData.filter = filterViewModels(group, defaultFrequency);
        linkedGuid.onChange = (guid: string, value: IValueViewModel) => {
            const isAggregated = selectedItem.aggregationMode !== 0;
            if (isAggregated && !hasNoVariables && selectedItem.isCreating) {
                selectedItem.variableTypeGuid = value.variableTypeGuid;
                configureVariableTypeProperties(properties, selectedItem);
                selectedItem.unitGuid = value.unitGuid;
                selectedItem.variableSubTypeGuid = value.variableSubTypeGuid;
                selectedItem.qualifierGuid = value.qualifierGuid;
                // control.$validate();
            }
        };
    }

    const aggregationPeriodField = _.find(properties, p => p.modelName === 'aggregationPeriod');
    if (aggregationPeriodField) {
        aggregationPeriodField.hide = !selectedItem.aggregationMode;
    }

    const isRollingField = _.find(properties, p => p.modelName === 'isRolling');
    if (isRollingField) {
        isRollingField.hide = !selectedItem.aggregationMode;
    }

    const aggregationMode = _.find(properties, p => p.modelName === 'aggregationMode');
    // #30937 do not hide any properties because of aggregation
    if (aggregationMode) {
        const isAggregated = selectedItem.aggregationMode !== 0;
        if (linkedGuid) {
            linkedGuid.hide = !isAggregated;
        }
        aggregationMode.onChange = value => {
            const isAggregated = value !== 0;
            if (linkedGuid) {
                linkedGuid.hide = !isAggregated;
            }
            if (!!isRollingField) {
                isRollingField.hide = !isAggregated;
            }
            if (!!aggregationPeriodField) {
                aggregationPeriodField.hide = !isAggregated;
            }
        };

    }

    // Recompute properties for digital
    const dataTypeField = _.find(properties, p => p.modelName === actualDataTypeName);
    dataTypeField.extraData.disablePreviousStep = true;
    const digitalField = _.find(properties, p => p.modelName === DigitalSetGuid.modelName);
    digitalField.hide = !isDigital;
    digitalField.required = isDigital;
    if (isDigital) {
        const sets = (digitalSets ?? []).map(s => { return { key: s.guid, value: s.name }; });
        digitalField.extraData = { options: sets };
    } else {
        digitalField.hide = true;
    }

    // Recompute properties for timestamp
    const fieldsToHide = [
        targetProperty,
        _.find(properties, p => p.modelName === Fields.DefaultValue.modelName),
        aggregationMode,
        _.find(properties, p => p.modelName === Fields.Resolution.modelName),
        _.find(properties, p => p.modelName === Fields.ResolutionPercentage.modelName),
        _.find(properties, p => p.modelName === Fields.Min.modelName),
        _.find(properties, p => p.modelName === Fields.Max.modelName)
    ];
    fieldsToHide.forEach(f => f.hide = isTimestamp);

    return properties;
}